import React, { useCallback, useEffect, useRef, useState } from "react";
import styled from "@emotion/styled";
import { useAppSelector } from "../../store/hooks";
import RenderChildren from "./RenderChildren";
import { useShoppingCart } from "./context/ShoppingCartContext";
import type { Product } from "./interfaces/catalogProduct";
import useEmblaCarousel from "embla-carousel-react";
import usePageLanguage from "./hooks/usePageLanguage";
import { pf, resolveAbsoluteAssetUrl } from "./utilities/helpers";
import {
  getCatalogProductDisplayFields,
  getProductPageBaseHref,
  resolveCatalogProductPricing,
} from "./utilities/catalogProductAdapter";
import {
  buildCatalogProductDetailHref,
  getVariantPriceRange,
  hasVariants,
} from "./utilities/productCatalog";
import {
  getComponentFromStructureMap,
  matchesEditorTarget,
} from "./utilities/componentIdentity";
import AutoScroll from "embla-carousel-auto-scroll";
import AutoPlay from "embla-carousel-autoplay";

import "./MediaSliderComponent.css";
import { ArrowBackIos, ArrowForwardIos } from "@mui/icons-material";

const Div = styled("div")(
  null,
  (props) =>
    function (props: any) {
      return { ...props["data-style"] };
    }
);

const languageTranslations = {
  et: {
    view_product: "VAATA TOODET",
  },
  en: {
    view_product: "VIEW PRODUCT",
  },
};

export default function ProductSliderComponent({
  id,
  uuid,
  attributes,
  childElements,
  childWebpageComponentRelations,
}) {
  const editorId = useAppSelector((state) => state.textEditor?.get("id"));
  const component = (useAppSelector((state) =>
    getComponentFromStructureMap(state.builder.structure.components, uuid ?? id)
  ) as any) ?? ({
    id: typeof id === "number" ? id : -1,
    parent_id: null,
    childElements: childElements ?? [],
    childWebpageComponentRelations: childWebpageComponentRelations ?? [],
    sort_order: 0,
    type: "ProductSliderComponent",
    uuid: uuid ?? null,
    version: 1,
    webpage_id: 0,
    attributes,
  } as any);
  const { listableCatalogProducts, pricingNow } = useShoppingCart();
  const lang = usePageLanguage();

  const getListPrice = (product: Product) => {
    const range = getVariantPriceRange(product);
    if (range) {
      return range.minPrice;
    }
    return resolveCatalogProductPricing(product, pricingNow).price_total;
  };

  const getSortName = (product: Product) =>
    getCatalogProductDisplayFields(product, lang).displayName;

  const languages = useAppSelector(
    (state) => state.builder.languageSettings.activeLanguages
  );

  const [productVariable, setProductVariable] = useState(
    component.attributes.productVariable
      ? component.attributes.productVariable
      : "category"
  );

  const containerRef = useRef<any>(null);

  const [slideCount, setSlidecount] = useState(
    component.attributes.styles.backgroundImage
      ? component.attributes.styles.backgroundImage.split(",").length
      : 0
  );

  const [selectedIndex, setSelectedIndex] = useState(0);

  const [thumbsAllowed, setThumbsAllowed] = useState(
    component.attributes.thumbs ? component.attributes.thumbs : false
  );
  const logSlidesInView = useCallback((emblaApi: any) => {
    // Set the currently active selected index
    setSelectedIndex(emblaApi.selectedScrollSnap());
  }, []);

  const [emblaThumbsRef, emblaThumbsApi] = useEmblaCarousel({
    containScroll: "keepSnaps",
    dragFree: true,
  });

  const [emblaMainRef, emblaMainApi] = useEmblaCarousel(
    {
      loop: component.attributes.loop ? component.attributes.loop : false,
      align: component.attributes.slideAlign
        ? component.attributes.slideAlign
        : "center",
    },

    // @ts-ignore
    component.attributes &&
      component.attributes.autoplay &&
      component.attributes.autoplay !== "none"
      ? [
          component.attributes.autoplay == "autoplay"
            ? AutoPlay({
                playOnInit: true,
                delay: component.attributes.autoplayInterval
                  ? component.attributes.autoplayInterval
                  : 3000,
                stopOnInteraction: false,
                stopOnMouseEnter: true,
              })
            : AutoScroll({
                speed: component.attributes.autoscrollSpeed
                  ? component.attributes.autoscrollSpeed
                  : 1,
                stopOnInteraction: false,
              }),
        ]
      : []
  );

  useEffect(() => {
    if (emblaMainApi) {
      emblaMainApi.on("slidesInView", logSlidesInView);
    }
  }, [emblaMainApi, logSlidesInView]);

  const onSelect = useCallback(() => {
    if (!emblaMainApi || !emblaThumbsApi) return;
    setSelectedIndex(emblaMainApi.selectedScrollSnap());
    emblaThumbsApi.scrollTo(emblaMainApi.selectedScrollSnap());
  }, [emblaMainApi, emblaThumbsApi, setSelectedIndex]);

  useEffect(() => {
    if (!emblaMainApi) return;
    onSelect();
    emblaMainApi.on("select", onSelect);
    emblaMainApi.on("reInit", onSelect);
  }, [emblaMainApi, onSelect, setSelectedIndex]);

  const [productCategory, setProductCategory] = useState(
    component.attributes.productCategory
      ? component.attributes.productCategory
      : null
  );

  const pages = useAppSelector((state) => state.builder.pages.list);
  const [selectedProducts, setSelectedProducts] = useState<Product[]>([]);
  const productPage = pages.find(
    (page) => page.pageType === "product" && page.languageCode === lang
  );

  const languageProductHref = getProductPageBaseHref(productPage, languages);

  function categoryExistsInProduct(
    product: Product,
    categoryIds: string[] | undefined
  ) {
    if (!categoryIds?.length) {
      return false;
    }
    return (product.productCategories ?? []).some((category) =>
      categoryIds.includes(category.uuid)
    );
  }

  useEffect(() => {
    const productUuids: string[] =
      component.attributes.productUuid?.length > 0
        ? component.attributes.productUuid
        : [];

    if (
      component.attributes.productVariable === "name" &&
      productUuids.length > 0
    ) {
      const products = listableCatalogProducts
        .filter((x) => productUuids.includes(x.productUuid))
        .sort(
          (a, b) =>
            productUuids.indexOf(a.productUuid) -
            productUuids.indexOf(b.productUuid)
        );
      setSelectedProducts(products);
      setSlidecount(products.length);
      return;
    }

    if (component.attributes.productVariable === "category") {
      const sortOption = component.attributes.categorySort;
      const products = listableCatalogProducts
        .filter((x) =>
          categoryExistsInProduct(x, component.attributes.categoryId)
        )
        .sort((a, b) => {
          if (sortOption === "a-z" || !sortOption) {
            return getSortName(a).localeCompare(getSortName(b));
          }
          if (sortOption === "z-a") {
            return getSortName(b).localeCompare(getSortName(a));
          }
          if (sortOption === "cheapest") {
            return getListPrice(a) - getListPrice(b);
          }
          if (sortOption === "expensive") {
            return getListPrice(b) - getListPrice(a);
          }
          return getSortName(a).localeCompare(getSortName(b));
        });

      setSelectedProducts(products);
      setSlidecount(products.length);
    }
  }, [
    listableCatalogProducts,
    pricingNow,
    productVariable,
    productCategory,
    component,
    component.attributes,
    lang,
  ]);

  const [emblaContainerWidth, setEmblaContainerWidth] = useState(0);

  const scrollPrev = useCallback(() => {
    if (emblaMainApi) {
      emblaMainApi.scrollPrev();
      setSelectedIndex(emblaMainApi.selectedScrollSnap());
    }
  }, [emblaMainApi]);

  const scrollNext = useCallback(() => {
    if (emblaMainApi) {
      emblaMainApi.scrollNext();
      setSelectedIndex(emblaMainApi.selectedScrollSnap());
    }
  }, [emblaMainApi]);

  useEffect(() => {
    if (containerRef.current?.clientWidth) {
      setEmblaContainerWidth(containerRef.current?.clientWidth);
    }
  }, [containerRef, containerRef.current?.clientWidth]);

  return (
    <Div
      id={id.toString()}
      className={
        (attributes.classes ?? "") +
        " builder-component builder-component__productSlider" +
        (matchesEditorTarget({ id, uuid }, editorId)
          ? " builder-component--active"
          : "")
      }
      style={{ ...attributes.styles, maxWidth: "100%" }}
      ref={containerRef}
    >
      <RenderChildren
        key={id + "children"}
        childWebpageComponentRelations={childWebpageComponentRelations.filter(
          (x) => x.sort === 1
        )}
      />
      <div
        className="embla"
        ref={emblaMainRef}
        style={{
          maxWidth: `100%`,
          position: "relative",
        }}
      >
        <div className="embla__container">
          {selectedProducts.map((selectedProduct) => {
            const parentPricing = resolveCatalogProductPricing(
              selectedProduct,
              pricingNow
            );
            const display = getCatalogProductDisplayFields(
              selectedProduct,
              lang
            );
            const displayImageUrl = resolveAbsoluteAssetUrl(display.firstFileUrl);
            const productDetailHref = buildCatalogProductDetailHref(
              languageProductHref,
              selectedProduct,
              lang
            );
            const priceRange = getVariantPriceRange(selectedProduct);
            const variantMode = hasVariants(selectedProduct);
            return (
            <div
              className="embla__slide"
              style={{
                maxWidth: `100%`,
                flex: `0 0 ${component.attributes.slideWidth}%`,
              }}
              key={selectedProduct.productUuid}
            >
              <div className="" style={{ maxHeight: "500px" }}>
                <div className="web-product" style={{ margin: "0px" }}>
                  <a
                    href={productDetailHref}
                    className="product-img"
                    style={{
                      backgroundImage: displayImageUrl
                        ? `url("${displayImageUrl}")`
                        : "none",
                      backgroundRepeat: "no-repeat",
                      backgroundSize: "contain",
                      backgroundPosition: "center",
                    }}
                  >
                    <img
                      src={displayImageUrl}
                      alt={display.displayName + " pilt."}
                      style={{ width: "100%" }}
                    ></img>
                    <div className="product-info-overlay">
                      <div
                        style={{ fontWeight: "400" }}
                        dangerouslySetInnerHTML={{
                          __html:
                            display.shortDescription,
                        }}
                      />
                    </div>
                  </a>
                  <div className="padding-10 pb-20">
                    <div className="product-name">
                      <a href={productDetailHref}>
                        {display.displayName}&nbsp;
                      </a>
                    </div>
                    <div className="productPrice">
                      {!variantMode && (
                        <>
                          <span
                            style={
                              parentPricing.activeAdjustments.length > 0
                                ? { textDecoration: "line-through" }
                                : {}
                            }
                          >
                            {parentPricing.activeAdjustments.length > 0
                              ? <>{pf(selectedProduct.priceRegular)}&euro;</>
                              : <>{pf(parentPricing.price_total)}&euro;</>}
                          </span>
                          {parentPricing.activeAdjustments.length > 0 ? (
                            <span className="discount-price-color">
                              {pf(Number(parentPricing.price_total))}&euro;
                            </span>
                          ) : null}
                        </>
                      )}
                      {variantMode && priceRange && (
                        <span>
                          {priceRange.minPrice !== priceRange.maxPrice
                            ? `${pf(priceRange.minPrice)} - ${pf(priceRange.maxPrice)}`
                            : pf(priceRange.minPrice)}{" "}
                          &euro;
                        </span>
                      )}
                    </div>
                    <div>
                      <a
                        href={productDetailHref}
                        className="btn btn-primary addProductToCart shop-controlled-border-radius single-product-button"
                        style={{
                          fontWeight: "600",
                          fontFamily: "Roboto, sans-serif",
                          padding: "6px 15px",
                          display: "inline-block",
                        }}
                      >
                        {languageTranslations[lang]?.view_product}
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
          })}
        </div>

        {selectedProducts.length > 1 && component.attributes.dots ? (
          <div>
            <div
              className="embla__dots"
              style={{
                display: "flex",
                textAlign: "center",
                alignItems: "center",
                justifyContent: "center",
                width: "100%",
                marginTop: "25px",
              }}
            >
              {selectedProducts.map((selectedProduct, index) => (
                <div
                  key={selectedProduct.productUuid}
                  onClick={() => {
                    if (emblaMainApi) {
                      setSelectedIndex(index);
                      emblaMainApi.scrollTo(index);
                    }
                  }}
                  style={{
                    paddingLeft: "6px",
                    paddingRight: "6px",
                    paddingBottom: "6px",
                    paddingTop: "6px",
                  }}
                >
                  <button
                    className="embla__dot"
                    style={{
                      height: "12px",
                      width: "12px",

                      background: "none",
                      border: "none",
                      outline: "none",
                      backgroundColor:
                        selectedIndex === index ? "#666" : "#ccc",
                      borderRadius: "50%",
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        ) : (
          ""
        )}

        {component.attributes.thumbs && (
          <>
            <button
              style={{
                display: selectedProducts.length > 1 ? "block" : "none",
                opacity: selectedProducts.length > 1 ? "1" : "0",
              }}
              className="embla__prev previous-carousel-button"
              onClick={scrollPrev}
            >
              <ArrowBackIos style={{ fontSize: "16px", marginLeft: "2px" }} />
            </button>
            <button
              style={{
                display: selectedProducts.length > 1 ? "block" : "none",
                opacity: selectedProducts.length > 1 ? "1" : "0",
              }}
              className="embla__next previous-carousel-button"
              onClick={scrollNext}
            >
              <ArrowForwardIos
                style={{ fontSize: "16px", marginRight: "2px" }}
              />
            </button>
          </>
        )}
      </div>
    </Div>
  );
}
